import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { DialogService } from '../../dialog/dialog.service';
import { ItemService } from '../../item/item.service';
import { SectionService } from '../../section/section.service';
import { Answer, Inspection, Question } from '../../shared/interfaces';
import { VersionService } from '../../version/version.service';
import { AnswerService } from '../answer.service';
import { AnswerStatus } from '../answer.status';
import { InspectionService } from '../inspection.service';
import { InspectionStatus } from '../inspection.status';

@Component({
  selector: 'app-inspection-review-invalid',
  templateUrl: './inspection-review-invalid.component.html',
  styleUrls: ['./inspection-review-invalid.component.scss'],
})
export class InspectionReviewInvalidComponent implements OnInit {
  constructor(
    public inspectionService: InspectionService,
    public itemService: ItemService,
    public answerService: AnswerService,
    public sectionService: SectionService,
    public versionService: VersionService,
    public route: ActivatedRoute,
    private toastr: ToastrService,
    public router: Router,
    private dialog: DialogService,
  ) {
    // not to loose popup reference even after navigating
    (window as any).getPopupReference = w => {
      this.popup = w;
    };
  }

  get selectedItemIndex() {
    return this._selectedItemIndex;
  }
  set selectedItemIndex(i) {
    this._selectedItemIndex = Math.max(0, Math.min(this.items.length - 1, i));
    this.sections = this.items[this._selectedItemIndex].sections;
    this.selectedSectionIndex = 0;
  }
  get selectedSectionIndex() {
    return this._selectedSectionIndex;
  }
  set selectedSectionIndex(i) {
    this._selectedSectionIndex = Math.max(0, Math.min(this.sections.length - 1, i));
    const section = this.sections[this._selectedSectionIndex];
    this.questions = section ? section.questions : [];
    this.selectedQuestionIndex = 0;

    // In case the first question of the section is hidden, move to the next
    if (!this.question.show) {
      this.next();
    }
  }
  get selectedQuestionIndex() {
    return this._selectedQuestionIndex;
  }
  set selectedQuestionIndex(i) {
    this._selectedQuestionIndex = Math.max(0, Math.min(this.questions.length - 1, i));
    this.question = this.questions[this._selectedQuestionIndex] || {};
    this.changePopup(this.question.answer);
    this.picturesAnswer = this.question.answer;
  }

  get showPictures() {
    return (
      this.picturesAnswer &&
      this.picturesAnswer.pictures &&
      this.picturesAnswer.pictures.length &&
      !this.forceMenu &&
      (!this.popup || this.popup.closed)
    );
  }

  public AnswerStatus = AnswerStatus;

  forceMenu = false;
  inspection: Inspection;
  items = [];
  picturesAnswer;

  _selectedItemIndex: number;

  sections = [];

  _selectedSectionIndex: number;

  questions = [];

  _selectedQuestionIndex: number;

  question = null;

  answers = [];

  isNotLatestVersion = false;

  popup: Window;

  versionName;

  versions;

  ngOnInit() {
    this.route.params.subscribe(async params => {
      if (!this.inspection) {
        this.inspection = await this.inspectionService.get(params.id);

        // Inspection must be completed before entering this screen
        if (!this.inspection.completed) {
          this.router.navigateByUrl(`/inspection/form/${this.inspection.id}`);
          return;
        }

        this.load();
      }
    });
  }

  get answersReadOnly() {
    if (this.inspection && this.inspection.status === InspectionStatus.NEEDS_REVIEW) {
      return false;
    }
    return true;
  }

  async load() {
    if (this.inspection.status !== InspectionStatus.INVALID_SYNC) {
      this.router.navigate([`/inspection/review/main/`, this.inspection.id], { queryParams: { right: 'overview' } });
      return;
    }

    await this.loadAnswers();
    await this.loadItems();
    this.mergeAnswers();
    this.setQuestionsVisibility();
    this.setSectionsVisibility();

    const latest = (await this.versionService.latest(this.inspection.version.name)).id;
    this.isNotLatestVersion = this.inspection.versionId !== latest;

    this.selectedItemIndex = 0;
    this.selectedSectionIndex = 0;
    this.selectedQuestionIndex = 0;

    this.items.forEach(i => {
      i.sections.forEach(s => this.setSectionErrors(s));
      this.setItemErrors(i);
    });
    this.resetQuestion();
  }

  async loadAnswers() {
    this.answers = await this.inspectionService.answers(this.inspection.id);
  }

  async loadItems() {
    this.items = [];

    const items = (await this.versionService.tree(this.inspection.versionId)).items;

    items.forEach(item => {
      this.items.push({
        sections: [],
        ...item,
        label: item.name,
      });
    });
  }

  // tslint:disable: forin
  mergeAnswers() {
    for (const iItem in this.items) {
      const sections = this.items[iItem].sections;

      for (const iSection in sections) {
        const questions = sections[iSection].questions;

        for (const iQuestion in questions) {
          const question = questions[iQuestion];

          question.answer = this.answers
            .filter(ans => ans.questionId === question.id)
            .sort((a, b) => (a.buildingIndex > b.buildingIndex ? 1 : -1));

          question.validation = this.inspection.validation
            ? this.inspection.validation.filter(x => x.questionId === question.id)
            : [];
        }
      }
    }
  }

  // tslint:disable: forin
  setQuestionsVisibility() {
    for (const iItem in this.items) {
      const sections = this.items[iItem].sections;

      for (const iSection in sections) {
        const questions = sections[iSection].questions;

        for (const iQuestion in questions) {
          questions[iQuestion].show = true;
          // Array.isArray(questions[iQuestion].answer) ? !!questions[iQuestion].answer.length : ('answer' in questions[iQuestion]);
        }
      }
    }
  }

  // tslint:disable: forin
  setSectionsVisibility() {
    for (const iItem in this.items) {
      const sections = this.items[iItem].sections;

      for (const iSection in sections) {
        const questions = sections[iSection].questions;
        sections[iSection].show = !!questions.filter(q => q.show).length;
      }
    }
  }

  resetQuestion() {
    this.forceMenu = false;
    this.picturesAnswer = this.question ? this.question.answer : null;
  }

  next() {
    // Increment question
    if (this.selectedQuestionIndex + 1 < this.questions.length) {
      this.selectedQuestionIndex++;
    } else {
      // Increment section
      if (this.selectedSectionIndex + 1 < this.sections.length) {
        this.selectedSectionIndex++;
      } else {
        // Increment item
        if (this.selectedItemIndex + 1 < this.items.length) {
          this.selectedItemIndex++;
        } else {
          // No questions left
          console.log('the end');

          // Move back to last displayable question
          // if (!this.question.show) {
          //   this.previous();
          // }
        }
      }
    }

    // Skip hidden questions
    // if (!this.question.show) {
    //   this.next();
    // }

    this.resetQuestion();
  }

  previous() {
    // Decrement question
    if (this.selectedQuestionIndex) {
      this.selectedQuestionIndex--;
    } else {
      // Decrement section
      if (this.selectedSectionIndex) {
        this.selectedSectionIndex--;
        this.selectedQuestionIndex = this.sections[this.selectedSectionIndex].questions.length - 1;
      } else {
        // Increment item
        if (this.selectedItemIndex) {
          this.selectedItemIndex--;
          this.selectedSectionIndex = this.items[this.selectedItemIndex].sections.length - 1;
          this.selectedQuestionIndex = this.sections[this.selectedSectionIndex].questions.length - 1;
        } else {
          // No questions left
          console.log('the end');

          // Move back to last displayable question
          if (!this.question.show) {
            this.next();
          }
        }
      }
    }

    // Skip hidden questions
    if (!this.question.show) {
      this.previous();
    }

    this.resetQuestion();
  }

  setSectionErrors(section) {
    let count = 0;
    const questions = section.questions;

    questions.forEach(q => {
      if (q.validation && q.validation.length) {
        count++;
      }
    });

    section.errors = count;
  }

  setItemErrors(item) {
    let sum = 0;

    item.sections.forEach(s => {
      sum += s.errors;
    });

    item.errors = sum;
  }

  getInspectionProgress() {
    if (this.items && this.items.length) {
      let sum = 0;
      this.items.forEach(i => {
        sum += i.progress || 0;
      });

      return sum / this.items.length;
    }
    return 0;
  }

  answerSetPictures(answer) {
    this.picturesAnswer = answer;
    this.forceMenu = false;
  }

  hasError(q) {
    return q?.validation?.length;
  }

  openPopup() {
    this.popup = window.open(location.origin + '/inspection/popup', 'LC+ Pictures', 'width=450, height=450');
    setTimeout(() => {
      try {
        this.changePopup(this.question.answer);
      } catch {}
    }, 1000);
  }

  changePopup(answer: Answer) {
    if (this.popup) {
      this.popup.postMessage(answer, '*');
    }
  }

  async validate() {
    console.log(this.inspection);
    this.inspection = await this.inspectionService.validate(this.inspection.id);
    this.load();
  }

  async removeAnswer(answerId) {
    this.dialog.confirm().show({
      message: 'Do you want to remove permanently this answer?',
      confirmed: async () => {
        this.question.answer = this.question.answer.filter(x => x.id !== answerId);
        await this.answerService.remove(answerId);
        this.toastr.success('Answer removed!');
      },
    });
  }

  async addAnswer() {
    const newAnswer = {
      questionId: this.question.id,
      questionVersion: this.question.version,
      inspectionId: this.inspection.id,
      buildingIndex: this.getNextBuildingIndex(this.question),
    };
    console.log(newAnswer);
    const saved = await this.answerService.add(newAnswer);
    this.question.answer.push(saved);
  }

  getNextBuildingIndex(question: Question) {
    try {
      const currentIndexes = question.answer.map(x => x.buildingIndex);

      // first non existing index or else let it be undefined
      for (let i = 1; ; i++) {
        if (currentIndexes.indexOf(i) < 0) {
          return i;
        }
      }
    } catch (e) {}
  }
}
